import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Nav = ({ isDarkMode, setIsDarkMode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? isDarkMode 
            ? 'bg-dark-bg/80 backdrop-blur-md border-b border-dark-border' 
            : 'bg-light-bg/80 backdrop-blur-md border-b border-light-border'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Link
              to="/"
              className={`text-2xl font-revive font-normal tracking-tight ${
                isDarkMode 
                  ? 'text-primary-25' 
                  : 'text-primary-1000'
              }`}
            >
              <span className="relative">
                Nayrix
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <motion.div key={link.to} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to={link.to}
                  className={`$${
                    isDarkMode 
                      ? 'text-dark-text-secondary hover:text-dark-text' 
                      : 'text-light-text-secondary hover:text-light-text'
                  } transition-colors duration-300`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
            <motion.button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-full ${
                isDarkMode 
                  ? 'text-dark-text hover:bg-dark-border' 
                  : 'text-light-text hover:bg-light-border'
              } transition-colors duration-300`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isDarkMode ? '🌞' : '🌙'}
            </motion.button>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/contact"
                className={`px-6 py-2 rounded-full ${
                  isDarkMode
                    ? 'bg-dark-accent hover:bg-dark-accent-hover text-dark-bg'
                    : 'bg-light-accent hover:bg-light-accent-hover text-light-bg'
                } transition-colors duration-300`}
              >
                Get Started
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className={`md:hidden p-2 ${
              isDarkMode ? 'text-dark-text' : 'text-light-text'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <div className="w-6 h-5 relative flex flex-col justify-between">
              <span className={`w-full h-0.5 ${
                isDarkMode ? 'bg-dark-text' : 'bg-light-text'
              } transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`w-full h-0.5 ${
                isDarkMode ? 'bg-dark-text' : 'bg-light-text'
              } transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
              <span className={`w-full h-0.5 ${
                isDarkMode ? 'bg-dark-text' : 'bg-light-text'
              } transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: '100vh' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`md:hidden fixed inset-0 top-16 ${
              isDarkMode 
                ? 'bg-dark-bg border-t border-dark-border' 
                : 'bg-light-bg border-t border-light-border'
            }`}
          >
            <div className="container mx-auto px-4 py-8">
              <div className="flex flex-col space-y-6">
                {navLinks.map((link) => (
                  <motion.div key={link.to} whileHover={{ x: 5 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      to={link.to}
                      className={`$${
                        isDarkMode 
                          ? 'text-dark-text-secondary hover:text-dark-text' 
                          : 'text-light-text-secondary hover:text-light-text'
                      } transition-colors duration-300 py-3 text-lg font-medium`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <div className="flex items-center justify-between pt-6 border-t border-neutral-200 dark:border-neutral-800">
                  <motion.button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className={`p-3 rounded-full ${
                      isDarkMode 
                        ? 'text-dark-text hover:bg-dark-border' 
                        : 'text-light-text hover:bg-light-border'
                    } transition-colors duration-300`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {isDarkMode ? '🌞' : '🌙'}
                  </motion.button>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/contact"
                      className={`px-8 py-3 rounded-full ${
                        isDarkMode
                          ? 'bg-dark-accent hover:bg-dark-accent-hover text-dark-bg'
                          : 'bg-light-accent hover:bg-light-accent-hover text-light-bg'
                      } transition-colors duration-300`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Nav; 